import React, { useState } from 'react';
import {
  AppBar, Toolbar, Button, Box, Menu, MenuItem,
  Drawer, List, ListItem, ListItemText, Collapse,
  ListItemButton, IconButton, Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

// New Icons matching mockup
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MovieCreationRoundedIcon from '@mui/icons-material/MovieCreationRounded';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import { GooglePlayIcon, AppleIcon } from './StoreIcons';
import { PLAY_STORE_URL, APP_STORE_URL, EDITOR_PLAY_STORE_URL, AI_PLAY_STORE_URL } from '../../constants/links';
import { AppQRScannerModal } from './AppQRScanner';

import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/filmhook_logo.svg';
import { useTheme } from '../../ThemeContext';

const navItems = [
  { label: 'Home', path: '/', icon: <HomeRoundedIcon sx={{ fontSize: '1.2rem' }} /> },
  { label: 'About', path: '/about', icon: <PersonOutlineRoundedIcon sx={{ fontSize: '1.2rem' }} /> },
  { label: 'Products', path: '/products', icon: <AppsRoundedIcon sx={{ fontSize: '1.2rem' }} /> },
  {
    label: 'Industry',
    icon: <MovieCreationRoundedIcon sx={{ fontSize: '1.2rem' }} />,
    children: [
      { label: 'Shooting Locations', path: '/industry/shooting-locations' },
      { label: 'Auditions & Casting', path: '/industry/auditions' },
      { label: 'Jobs & Crew Hiring', path: '/industry/jobs' },
      { label: 'Live Broadcast', path: '/industry/live' },
      { label: 'Own Media Editor', path: '/industry/own-media-editor' },
      { label: 'AI Assistant', path: '/industry/ai-assistant' },
      { label: 'AI Filters', path: '/industry/ai-filters' },
      { label: 'VFX Effects', path: '/industry/effects' },
      { label: 'Business Enterprise', path: '/industry/business' },
      { label: 'Global Marketing', path: '/industry/global-marketing' },
    ]
  },
  { label: 'Terms & Condition', path: '/termsAndCondition', icon: <ArticleOutlinedIcon sx={{ fontSize: '1.2rem' }} /> },
  { label: 'Privacy Policy', path: '/privacyPolicyAll', icon: <SecurityOutlinedIcon sx={{ fontSize: '1.2rem' }} /> },
  {
    label: 'Pages',
    icon: <LayersOutlinedIcon sx={{ fontSize: '1.2rem' }} />,
    children: [
      { label: 'Blog', path: '/blog' },
      { label: "FAQ's", path: '/faq' },
      { label: 'Age Suitability', path: '/age-suitability' },
      { label: 'Child Safety', path: '/child-safety' },
      { label: 'Data Safety', path: '/data-safety' },
      { label: 'Account Deletion', path: '/account-deletion' },
      { label: 'Manage Account', path: '/manage-account' },
      { label: 'Marketing Page', path: '/marketing' },
      { label: 'Terms of Service', path: '/terms-of-service' },
    ]
  },
  { label: 'Contact Us', path: '/contact', icon: <PhoneOutlinedIcon sx={{ fontSize: '1.2rem' }} /> }
];

// Reusable clip-path for chamfered corners
const chamferedClipPath = 'polygon(16px 0%, calc(100% - 16px) 0%, 100% 16px, 100% calc(100% - 16px), calc(100% - 16px) 100%, 16px 100%, 0% calc(100% - 16px), 0% 16px)';
const chamferedButtonClipPath = 'polygon(10px 0%, calc(100% - 10px) 0%, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0% calc(100% - 10px), 0% 10px)';

const Header = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubMenuLabel, setOpenSubMenuLabel] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrTargetId, setQrTargetId] = useState('filmhook-android');
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();

  const handleOpenQR = (targetId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    handleDownloadClose();
    setQrTargetId(targetId || 'filmhook-android');
    setQrModalOpen(true);
  };

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const toggleDrawer = (open) => () => setMobileOpen(open);

  const handleMenuOpen = (event, label) => {
    setAnchorEl(event.currentTarget);
    setHoveredMenu(label);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setHoveredMenu(null);
  };

  const toggleMobileSubMenu = (label) => {
    setOpenSubMenuLabel(prev => (prev === label ? null : label));
  };

  const isActive = (path) => {
    if (path === '/products') {
      return location.pathname === '/products' || location.pathname === '/features';
    }
    return location.pathname === path;
  };

  // Vertical Separator Component
  const VerticalSeparator = () => (
    <Box sx={{ 
      width: '1px', 
      height: '32px', 
      background: isDarkMode 
        ? 'linear-gradient(to bottom, transparent, rgba(7, 164, 245, 0.4), transparent)' 
        : 'linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1), transparent)',
      mx: 2
    }} />
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        background: 'transparent',
        boxShadow: 'none',
        mt: 3,
        mx: 'auto',
        maxWidth: '100%',
        width: 'calc(100% - 32px)',
        zIndex: 1100,
        // The drop-shadow is applied to the wrapper so it follows the clip-path of the child
        filter: isDarkMode 
          ? 'drop-shadow(0 0 15px rgba(7, 164, 245, 0.3)) drop-shadow(0 8px 20px rgba(0,0,0,0.6))' 
          : 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.08))',
      }}
    >
      {/* Sci-fi Chamfered Container */}
      <Box sx={{
        position: 'relative',
        background: isDarkMode ? '#050a14' : '#ffffff',
        backdropFilter: 'blur(30px)',
        WebkitBackdropFilter: 'blur(30px)',
        clipPath: chamferedClipPath,
        // Inner glowing border effect using pseudo-element inside the clipped area
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(7,164,245,0.4) 0%, transparent 20%, transparent 80%, rgba(7,164,245,0.4) 100%)' 
            : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.05) 100%)',
          zIndex: -1,
          padding: '1px',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          clipPath: chamferedClipPath,
        },
        // Top and Bottom center tech accents
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0, left: '50%',
          transform: 'translateX(-50%)',
          width: '120px',
          height: '2px',
          background: isDarkMode ? '#07a4f5' : '#3b82f6',
          boxShadow: isDarkMode ? '0 0 10px #07a4f5' : 'none',
        }
      }}> 
        {/* Bottom accent */}
        <Box sx={{
           position: 'absolute',
           bottom: 0, left: '50%',
           transform: 'translateX(-50%)',
           width: '120px',
           height: '3px',
           background: isDarkMode ? '#07a4f5' : '#e2e8f0',
           display: 'flex',
           justifyContent: 'center',
           alignItems: 'center',
           gap: '4px',
           clipPath: 'polygon(10px 0, calc(100% - 10px) 0, 100% 100%, 0 100%)'
        }}>
           {/* Tech dots/lines inside bottom accent */}
           <Box sx={{ width: '4px', height: '100%', background: isDarkMode ? '#050a14' : '#fff', transform: 'skewX(-30deg)' }} />
           <Box sx={{ width: '4px', height: '100%', background: isDarkMode ? '#050a14' : '#fff', transform: 'skewX(-30deg)' }} />
           <Box sx={{ width: '4px', height: '100%', background: isDarkMode ? '#050a14' : '#fff', transform: 'skewX(-30deg)' }} />
        </Box>

        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: '80px', px: { xs: 2, md: 1.5 } }}>
          
          {/* Logo */}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={logo} alt="Filmhook" style={{ height: '40px', filter: isDarkMode ? 'drop-shadow(0 0 8px rgba(255,255,255,0.15))' : 'none' }} />
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2 }}>
            <VerticalSeparator />
            
            {navItems.map((item) =>
              item.children ? (
                <Box
                  key={item.label}
                  onMouseEnter={(e) => handleMenuOpen(e, item.label)}
                  onMouseLeave={handleMenuClose}
                  sx={{ position: 'relative' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      height: '70px',
                      minWidth: '80px',
                      px: 2,
                      color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        color: isDarkMode ? '#fff' : '#0f172a',
                      },
                    }}
                  >
                    {item.icon}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mt: 0.5 }}>{item.label}</Typography>
                      <ExpandMore sx={{ fontSize: '1rem', ml: 0.2 }} />
                    </Box>
                  </Box>
                  <Menu
                    anchorEl={anchorEl}
                    open={hoveredMenu === item.label}
                    onClose={handleMenuClose}
                    MenuListProps={{ onMouseLeave: handleMenuClose }}
                    PaperProps={{
                      sx: {
                        background: isDarkMode ? '#0a0f1a' : '#ffffff',
                        backdropFilter: 'blur(16px)',
                        border: isDarkMode ? '1px solid rgba(7, 164, 245, 0.3)' : '1px solid rgba(0,0,0,0.08)',
                        borderRadius: '0px',
                        clipPath: chamferedButtonClipPath,
                        mt: 1.5,
                      }
                    }}
                  >
                    {item.children.map((child) => (
                      <MenuItem
                        key={child.label}
                        component={Link}
                        to={child.path}
                        onClick={handleMenuClose}
                        sx={{
                          color: isActive(child.path) ? '#07a4f5' : (isDarkMode ? 'rgba(255,255,255,0.85)' : '#1e293b'),
                          fontWeight: isActive(child.path) ? 700 : 500,
                          fontSize: '0.85rem',
                          py: 1,
                          px: 3,
                          '&:hover': { color: '#07a4f5', background: isDarkMode ? 'rgba(7,164,245,0.1)' : 'rgba(7,164,245,0.05)' },
                        }}
                      >
                        {child.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              ) : (
                <Box
                  key={item.label}
                  component={Link}
                  to={item.path}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    gap: 0.5,
                    height: '70px',
                    minWidth: '80px',
                    px: 2,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    // Active State Styling (Chamfered Glowing Box)
                    ...(isActive(item.path) ? {
                      color: '#07a4f5',
                      clipPath: chamferedButtonClipPath,
                      background: isDarkMode ? 'rgba(7, 164, 245, 0.05)' : '#f8fafc',
                      // Inner glow and border for active item
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: isDarkMode ? '#07a4f5' : '#e2e8f0',
                        zIndex: -1,
                        padding: '1px',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                      },
                      // The glowing bottom dash
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '12px',
                        width: '16px',
                        height: '2px',
                        background: '#07a4f5',
                        boxShadow: isDarkMode ? '0 0 8px #07a4f5' : 'none',
                      }
                    } : {
                      color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569',
                      '&:hover': {
                        color: isDarkMode ? '#fff' : '#0f172a',
                      }
                    })
                  }}
                >
                  {item.icon}
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: isActive(item.path) ? 700 : 600, mt: 0.5 }}>
                    {item.label}
                  </Typography>
                </Box>
              )
            )}
            
            <VerticalSeparator />
          </Box>

          {/* Right Side Tools */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            
            {/* Target-style Light/Dark Mode Switcher */}
            <Box sx={{
               position: 'relative',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '44px',
               height: '44px',
               '&::before': {
                 content: '""',
                 position: 'absolute',
                 top: 0, left: 0, right: 0, bottom: 0,
                 border: isDarkMode ? '1px dashed rgba(255,255,255,0.2)' : '1px dashed rgba(0,0,0,0.2)',
                 borderRadius: '50%',
                 animation: 'spin 10s linear infinite',
               }
            }}>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  width: '32px',
                  height: '32px',
                  color: isDarkMode ? '#e2e8f0' : '#475569',
                  background: 'transparent',
                  '&:hover': {
                    color: isDarkMode ? '#07a4f5' : '#0f172a',
                  }
                }}
              >
                {isDarkMode ? <LightModeIcon sx={{ fontSize: '1.1rem' }} /> : <DarkModeIcon sx={{ fontSize: '1.1rem' }} />}
              </IconButton>
              <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
            </Box>

            {/* Chamfered Download App Button with Dropdown Menu */}
            <Box sx={{ filter: isDarkMode ? 'drop-shadow(0 4px 12px rgba(7,164,245,0.4))' : 'drop-shadow(0 4px 10px rgba(0,0,0,0.1))' }}>
              <Button
                onClick={handleDownloadClick}
                startIcon={<DownloadIcon />}
                endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: downloadAnchorEl ? 'rotate(180deg)' : 'none' }} />}
                sx={{
                  background: '#07a4f5',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  clipPath: chamferedButtonClipPath,
                  borderRadius: 0,
                  px: 2.8,
                  py: 1.2,
                  '&:hover': {
                    background: '#0284c7',
                  },
                }}
              >
                Download App
              </Button>
            </Box>

            {/* Store Options Dropdown Menu */}
            <Menu
              anchorEl={downloadAnchorEl}
              open={Boolean(downloadAnchorEl)}
              onClose={handleDownloadClose}
              PaperProps={{
                elevation: 8,
                sx: {
                  mt: 1.5,
                  borderRadius: '16px',
                  minWidth: 260,
                  p: 1,
                  background: isDarkMode ? '#0d1530' : '#ffffff',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(7, 164, 245,0.2)',
                  boxShadow: isDarkMode ? '0 16px 40px rgba(0,0,0,0.6)' : '0 16px 40px rgba(7,164,245,0.18)',
                  '& .MuiMenuItem-root': {
                    borderRadius: '12px',
                    py: 1.2,
                    px: 2,
                    gap: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: isDarkMode ? 'rgba(7, 164, 245, 0.15)' : 'rgba(7, 164, 245, 0.08)',
                    }
                  }
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem
                component="a"
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDownloadClose}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    background: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <GooglePlayIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b', display: 'block', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Get it on
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: isDarkMode ? '#ffffff' : '#0d1530', fontSize: '0.9rem' }}>
                      Google Play Store
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleOpenQR('filmhook-android', e)}
                  title="Scan QR Code"
                  sx={{
                    color: '#07a4f5',
                    bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : 'rgba(7, 164, 245, 0.08)',
                    '&:hover': { bgcolor: '#07a4f5', color: '#fff' },
                  }}
                >
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </MenuItem>

              <MenuItem
                component="a"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDownloadClose}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    background: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isDarkMode ? '#ffffff' : '#0d1530',
                    flexShrink: 0
                  }}>
                    <AppleIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b', display: 'block', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Download on
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: isDarkMode ? '#ffffff' : '#0d1530', fontSize: '0.9rem' }}>
                      Apple App Store (iOS)
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleOpenQR('filmhook-ios', e)}
                  title="Scan QR Code"
                  sx={{
                    color: '#38bdf8',
                    bgcolor: isDarkMode ? 'rgba(56, 189, 248, 0.12)' : 'rgba(56, 189, 248, 0.08)',
                    '&:hover': { bgcolor: '#38bdf8', color: '#fff' },
                  }}
                >
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </MenuItem>

              <MenuItem
                component="a"
                href={EDITOR_PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDownloadClose}
                sx={{
                  borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pr: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    background: 'rgba(139, 92, 246, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#a855f7',
                    flexShrink: 0
                  }}>
                    <GooglePlayIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#a855f7', display: 'block', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Video Editing Suite
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: isDarkMode ? '#ffffff' : '#0d1530', fontSize: '0.9rem' }}>
                      Filmhook Editor (Play Store)
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleOpenQR('filmhook-editor', e)}
                  title="Scan QR Code"
                  sx={{
                    color: '#a855f7',
                    bgcolor: isDarkMode ? 'rgba(168, 85, 247, 0.12)' : 'rgba(168, 85, 247, 0.08)',
                    '&:hover': { bgcolor: '#a855f7', color: '#fff' },
                  }}
                >
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </MenuItem>

              <MenuItem
                component="a"
                href={AI_PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDownloadClose}
                sx={{
                  borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pr: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    background: 'rgba(6, 182, 212, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#06b6d4',
                    flexShrink: 0
                  }}>
                    <GooglePlayIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#06b6d4', display: 'block', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Intelligent Cinema AI
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: isDarkMode ? '#ffffff' : '#0d1530', fontSize: '0.9rem' }}>
                      Filmhook AI (Play Store)
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleOpenQR('filmhook-ai', e)}
                  title="Scan QR Code"
                  sx={{
                    color: '#06b6d4',
                    bgcolor: isDarkMode ? 'rgba(6, 182, 212, 0.12)' : 'rgba(6, 182, 212, 0.08)',
                    '&:hover': { bgcolor: '#06b6d4', color: '#fff' },
                  }}
                >
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </MenuItem>

              {/* Bottom Quick Scanner Item */}
              <MenuItem
                onClick={(e) => handleOpenQR('filmhook-android', e)}
                sx={{
                  borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9',
                  bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.08)' : 'rgba(7, 164, 245, 0.05)',
                  py: 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  '&:hover': {
                    bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.15)' : 'rgba(7, 164, 245, 0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    bgcolor: '#07a4f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    flexShrink: 0,
                  }}
                >
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={800} sx={{ color: '#07a4f5', fontSize: '0.86rem' }}>
                    Scan QR to Install
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.72rem', display: 'block' }}>
                    Point camera at live QR scanner
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>

          {/* Mobile Menu Icon */}
          <IconButton
            sx={{ display: { xs: 'block', lg: 'none' }, color: isDarkMode ? 'white' : '#0d1530' }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Drawer */}
          <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer(false)}>
            <Box
              sx={{
                width: 270,
                p: 2,
                background: isDarkMode ? '#050a14' : '#ffffff',
                minHeight: '100vh',
                borderLeft: isDarkMode ? '1px solid rgba(7, 164, 245,0.4)' : '1px solid rgba(0,0,0,0.05)',
              }}
            > 
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pl: 1 }}>
                <img src={logo} alt="Filmhook" style={{ height: '40px' }} />
              </Box>
              <List>
                {navItems.map((item) =>
                  item.children ? (
                    <Box key={item.label}>
                      <ListItemButton
                        onClick={() => toggleMobileSubMenu(item.label)}
                        sx={{ borderRadius: '12px', mb: 0.5 }}
                      >
                        <Box sx={{ mr: 2, color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569', display: 'flex' }}>
                          {item.icon}
                        </Box>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ sx: { color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#0f172a', fontWeight: 600 } }}
                        />
                        {openSubMenuLabel === item.label
                          ? <ExpandLess sx={{ color: '#07a4f5' }} />
                          : <ExpandMore sx={{ color: 'rgba(255,255,255,0.3)' }} />}
                      </ListItemButton>
                      <Collapse in={openSubMenuLabel === item.label} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {item.children.map((child) => (
                            <ListItem
                              button
                              key={child.label}
                              component={Link}
                              to={child.path}
                              onClick={toggleDrawer(false)}
                              sx={{
                                pl: 6,
                                borderRadius: '12px',
                                color: isActive(child.path) ? '#07a4f5' : (isDarkMode ? 'rgba(255,255,255,0.7)' : '#475569'),
                                '&:hover': { color: '#07a4f5', background: isDarkMode ? 'rgba(7,164,245,0.1)' : 'rgba(7,164,245,0.05)' },
                              }}
                            >
                              <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive(child.path) ? 700 : 500 }} />
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  ) : (
                    <ListItem
                      button
                      key={item.label}
                      component={Link}
                      to={item.path}
                      onClick={toggleDrawer(false)}
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        color: isActive(item.path) ? '#07a4f5' : (isDarkMode ? 'rgba(255,255,255,0.85)' : '#0f172a'),
                        clipPath: isActive(item.path) ? chamferedButtonClipPath : 'none',
                        background: isActive(item.path) ? (isDarkMode ? 'rgba(7,164,245,0.1)' : 'rgba(7,164,245,0.05)') : 'transparent',
                        '&:hover': { color: '#07a4f5', background: isDarkMode ? 'rgba(7,164,245,0.1)' : 'rgba(7,164,245,0.05)' },
                      }}
                    >
                      <Box sx={{ mr: 2, color: 'inherit', display: 'flex' }}>
                        {item.icon}
                      </Box>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: isActive(item.path) ? 700 : 600 }} />
                    </ListItem>
                  )
                )}

                {/* Mobile Mode Switcher */}
                <ListItem sx={{ mt: 2, px: 2 }}>
                   <Button 
                     fullWidth
                     variant="outlined" 
                     onClick={toggleTheme}
                     startIcon={isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                     sx={{ 
                       clipPath: chamferedButtonClipPath,
                       borderRadius: 0, 
                       textTransform: 'none',
                       borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                       color: isDarkMode ? '#fff' : '#0f172a',
                       py: 1
                     }}
                   >
                     {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                   </Button>
                </ListItem>

                {/* Download App (Mobile) */}
                <ListItem sx={{ mt: 1, px: 2, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'stretch' }}>
                  <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b', fontWeight: 700, px: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Download Filmhook App
                  </Typography>
                  <Button
                    fullWidth
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<GooglePlayIcon size={20} />}
                    sx={{ 
                      borderRadius: '12px', 
                      background: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                      border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.1)',
                      color: isDarkMode ? 'white' : '#0d1530',
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1.2,
                      px: 2,
                      justifyContent: 'flex-start',
                      '&:hover': {
                        background: '#07a4f5',
                        color: 'white',
                      }
                    }}
                  >
                    Google Play Store
                  </Button>
                  <Button
                    fullWidth
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<AppleIcon size={20} />}
                    sx={{ 
                      borderRadius: '12px', 
                      background: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                      border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.1)',
                      color: isDarkMode ? 'white' : '#0d1530',
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1.2,
                      px: 2,
                      justifyContent: 'flex-start',
                      '&:hover': {
                        background: '#07a4f5',
                        color: 'white',
                      }
                    }}
                  >
                    Apple App Store (iOS)
                  </Button>
                  <Button
                    fullWidth
                    href={EDITOR_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<GooglePlayIcon size={20} />}
                    sx={{ 
                      borderRadius: '12px', 
                      background: isDarkMode ? 'rgba(139, 92, 246, 0.12)' : '#faf5ff',
                      border: isDarkMode ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(139, 92, 246, 0.25)',
                      color: isDarkMode ? '#c084fc' : '#7c3aed',
                      textTransform: 'none',
                      fontWeight: 800,
                      py: 1.2,
                      px: 2,
                      justifyContent: 'flex-start',
                      '&:hover': {
                        background: '#8b5cf6',
                        color: 'white',
                      }
                    }}
                  >
                    Filmhook Editor (Play Store)
                  </Button>
                  <Button
                    fullWidth
                    href={AI_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<GooglePlayIcon size={20} />}
                    sx={{ 
                      borderRadius: '12px', 
                      background: isDarkMode ? 'rgba(6, 182, 212, 0.12)' : '#ecfeff',
                      border: isDarkMode ? '1px solid rgba(6, 182, 212, 0.3)' : '1px solid rgba(6, 182, 212, 0.25)',
                      color: isDarkMode ? '#22d3ee' : '#0891b2',
                      textTransform: 'none',
                      fontWeight: 800,
                      py: 1.2,
                      px: 2,
                      justifyContent: 'flex-start',
                      '&:hover': {
                        background: '#06b6d4',
                        color: 'white',
                      }
                    }}
                  >
                    Filmhook AI (Play Store)
                  </Button>
                  <Button
                    fullWidth
                    onClick={() => {
                      setMobileOpen(false);
                      setQrModalOpen(true);
                    }}
                    startIcon={<QrCodeScannerRoundedIcon sx={{ fontSize: 20 }} />}
                    sx={{ 
                      borderRadius: '12px', 
                      background: '#07a4f5',
                      color: '#ffffff',
                      textTransform: 'none',
                      fontWeight: 800,
                      py: 1.2,
                      px: 2,
                      justifyContent: 'center',
                      boxShadow: '0 4px 15px rgba(7, 164, 245, 0.4)',
                      mt: 0.5,
                      '&:hover': {
                        background: '#068bc2',
                      }
                    }}
                  >
                    Scan QR with Camera
                  </Button>
                </ListItem>
              </List>
            </Box>
          </Drawer>
        </Toolbar>
      </Box>

      {/* Global QR Scanner Modal */}
      <AppQRScannerModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        defaultAppId={qrTargetId}
      />
    </AppBar>
  );
};

export default Header;



